import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import { MaskLine, Reveal } from "@/components/Reveal";
import { Counter } from "@/components/Counter";
import { Ribbon } from "@/components/Ribbon";
import { CTASection, SectionHeading } from "@/components/Bits";
import {
  STATS, CAPABILITIES, MATERIALS, PRODUCT_PHOTOS, INDUSTRIES,
  WHY_CHOOSE, PROCESS, IMAGES, LOGO,
} from "@/lib/data";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden text-white" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroCnc} alt="CNC machining" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F17] via-[#0A0F17]/80 to-[#0A0F17]/50" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F17]/80 to-transparent" />
      </motion.div>

      <div className="relative max-w-[1500px] w-full mx-auto px-5 md:px-10 pt-32 pb-16">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="font-mono text-xs tracking-[0.22em] uppercase text-slate-300 mb-8">
          Est. 1969 · Ahmedabad, Gujarat
        </motion.div>

        <h1 className="font-display font-extrabold uppercase tracking-tighter text-[13vw] sm:text-6xl lg:text-[6rem] leading-[0.86]">
          <MaskLine delay={0.15}>55+ Years of</MaskLine>
          <MaskLine delay={0.3}>Pattern Making</MaskLine>
          <MaskLine delay={0.45}>& Manufacturing</MaskLine>
          <MaskLine delay={0.6}><span className="text-[#60A5FA]">Excellence.</span></MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
          className="mt-10 font-mono text-xs md:text-sm tracking-wide text-slate-300 max-w-3xl uppercase"
        >
          72,000+ Patterns Manufactured / Industrial Patterns / Dies & Tooling / CNC Machining / Engineering Solutions
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05 }} className="mt-10 flex flex-col sm:flex-row gap-4">
          <Link to="/enquiry" data-testid="hero-request-quote" className="btn-primary !bg-white !text-[#0E1626] hover:!bg-[#1D4ED8] hover:!text-white">Request A Quote <ArrowUpRight size={16} /></Link>
          <Link to="/capabilities" data-testid="hero-explore-capabilities" className="btn-outline !border-[#475569] !text-white hover:!border-white hover:!text-white">Explore Our Capabilities</Link>
        </motion.div>
      </div>

      <div className="relative border-t border-white/10 backdrop-blur-sm bg-[#0A0F17]/40">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
          {STATS.map((s, i) => (
            <div key={i} className="py-8 px-4 first:pl-0" data-testid={`hero-stat-${i}`}>
              <div className="font-display font-extrabold text-4xl md:text-5xl tracking-tight">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-2 font-mono text-[10px] md:text-xs tracking-widest uppercase text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const About = () => (
  <section className="max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center" data-testid="about-preview">
    <div className="lg:col-span-6 relative group">
      <div className="overflow-hidden border border-[#DCE1E8] bg-white flex items-center justify-center h-[420px] md:h-[560px]">
        <img src={LOGO} alt="JM Mevada Pattern Works" className="w-2/3 max-w-[360px] object-contain" />
      </div>
      <div className="absolute -bottom-6 -right-2 md:right-6 bg-[#0E1626] text-white px-8 py-6">
        <div className="font-display font-extrabold text-4xl">55+</div>
        <div className="font-mono text-[10px] tracking-widest uppercase text-slate-300">Years</div>
      </div>
    </div>
    <div className="lg:col-span-6">
      <SectionHeading index="01 / About Us" title="A Family Legacy in Pattern Making." />
      <Reveal delay={0.1}>
        <p className="mt-8 text-lg text-slate-600 font-light leading-relaxed">
          Started by Jagjivanbhai Mevada as JM Mevada & Co., the business has continued through generations. Today, Dilipbhai Mevada carries forward the experience and values developed over more than five decades.
        </p>
        <p className="mt-5 text-lg text-slate-600 font-light leading-relaxed">
          With 72,000+ patterns manufactured, the company continues to provide reliable pattern-making and engineering solutions to industrial customers.
        </p>
        <Link to="/about" data-testid="about-read-more" className="btn-outline mt-10">Read More <ArrowRight size={16} /></Link>
      </Reveal>
    </div>
  </section>
);

const Capabilities = () => (
  <section className="border-t border-[#DCE1E8] bg-white" data-testid="capabilities-preview">
    <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-32">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
        <SectionHeading index="02 / Capabilities" title="What We Manufacture" />
        <Link to="/capabilities" data-testid="view-capabilities" className="btn-outline w-fit">View Capabilities <ArrowRight size={16} /></Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-l border-t border-[#DCE1E8]">
        {CAPABILITIES.map((c, i) => (
          <Reveal key={i} delay={(i % 4) * 0.05}>
            <div className="group border-r border-b border-[#DCE1E8] p-8 h-full hover:bg-[#F4F6F9] transition-colors duration-300" data-testid={`capability-card-${i}`}>
              <div className="font-mono text-xs text-[#1D4ED8] mb-8">{String(i + 1).padStart(2, "0")}</div>
              <div className="font-display font-bold text-xl tracking-tight leading-tight text-[#0E1626]">{c}</div>
              <ArrowUpRight size={18} className="mt-4 text-slate-400 group-hover:text-[#1D4ED8] transition-colors duration-300" />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Materials = () => (
  <section className="max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-32 grid lg:grid-cols-12 gap-12" data-testid="materials-section">
    <div className="lg:col-span-5">
      <SectionHeading index="03 / Materials" title="Built According To Your Requirement" />
      <Reveal delay={0.1}>
        <p className="mt-8 text-lg text-slate-600 font-light leading-relaxed">
          We manufacture patterns according to customer drawings, samples, dimensions and technical requirements.
        </p>
      </Reveal>
    </div>
    <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-4">
      {MATERIALS.map((m, i) => (
        <Reveal key={i} delay={i * 0.05}>
          <div className="group relative border border-[#DCE1E8] aspect-square overflow-hidden" data-testid={`material-${i}`}>
            <img src={m.img} alt={m.name} className="absolute inset-0 w-full h-full object-cover img-grayscale group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F17] via-[#0A0F17]/30 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-between p-6 text-white">
              <div className="font-mono text-sm text-[#93C5FD]">{m.code}</div>
              <div className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">{m.name}</div>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Products = () => (
  <section className="border-t border-[#DCE1E8] bg-white" data-testid="products-preview">
    <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-32">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
        <SectionHeading index="04 / Products" title="Precision Patterns, Delivered." />
        <Link to="/products" data-testid="view-all-products" className="btn-outline w-fit">View All Products <ArrowRight size={16} /></Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {PRODUCT_PHOTOS.slice(0, 3).map((src, i) => (
          <Reveal key={i} delay={(i % 3) * 0.06}>
            <Link to="/products" className="group block overflow-hidden border border-[#DCE1E8] bg-white" data-testid={`product-preview-${i}`}>
              <img src={src} alt="Industrial pattern" className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-700" />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Industries = () => (
  <section className="max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-32" data-testid="industries-preview">
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
      <SectionHeading index="05 / Industries" title="Industries We Serve" />
      <Link to="/industries" data-testid="view-all-industries" className="btn-outline w-fit">View All Industries <ArrowRight size={16} /></Link>
    </div>
    <div className="flex flex-wrap gap-3">
      {INDUSTRIES.map((ind, i) => (
        <Reveal key={i} delay={(i % 6) * 0.04}>
          <span className="inline-block border border-[#DCE1E8] bg-white px-6 py-4 font-display font-bold text-lg md:text-xl tracking-tight hover:border-[#1D4ED8] hover:text-[#1D4ED8] transition-colors duration-300 text-[#0E1626]" data-testid={`industry-chip-${i}`}>
            {ind.title}
          </span>
        </Reveal>
      ))}
    </div>
  </section>
);

const WhyChoose = () => (
  <section className="border-t border-[#DCE1E8] bg-white" data-testid="why-choose-section">
    <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-32 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <SectionHeading index="06 / Why Us" title="Why Choose JM Mevada" />
      </div>
      <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-10">
        {WHY_CHOOSE.map((w, i) => (
          <Reveal key={i} delay={(i % 2) * 0.06}>
            <div className="flex items-center gap-4 py-6 border-b border-[#DCE1E8]" data-testid={`why-${i}`}>
              <Check size={20} className="text-[#1D4ED8] shrink-0" />
              <span className="font-display font-semibold text-lg md:text-xl tracking-tight text-[#0E1626]">{w}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-32" data-testid="process-section">
    <SectionHeading index="07 / Process" title="From Requirement to Delivery" className="mb-16" />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-[#DCE1E8]">
      {PROCESS.map((p, i) => (
        <Reveal key={i} delay={(i % 3) * 0.06}>
          <div className="border-r border-b border-[#DCE1E8] bg-white p-8 group hover:bg-[#F4F6F9] transition-colors duration-300 h-full" data-testid={`process-step-${i}`}>
            <div className="font-display font-extrabold text-6xl text-[#D3DAE3] group-hover:text-[#1D4ED8] transition-colors duration-300">{p.step}</div>
            <div className="font-display font-bold text-2xl tracking-tight mt-4 text-[#0E1626]">{p.name}</div>
            <p className="text-slate-600 font-light mt-3 leading-relaxed">{p.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export default function Home() {
  return (
    <div>
      <Hero />
      <Ribbon />
      <About />
      <Capabilities />
      <Materials />
      <Products />
      <Industries />
      <WhyChoose />
      <Process />
      <CTASection />
    </div>
  );
}
